import { PLATFORMS_CODES } from '@/constants/languages'
import { IShotlist } from '@/types/shotlist.interface'
import type {
	ISelectedPlatform,
	TPlatformHeaderKind,
	TPlatformProcessingType,
	TPlatformResult,
	TPlatformValue
} from '@/types/uploadForm.interface'

export const getLinkedPlatform = (
	value: TPlatformValue
): TPlatformValue | null => {
	if (value === 'english') return 'main-english'
	if (value === 'main-english') return 'english'
	return null
}

export const getPlatformResultFromShotlist = ({
	value,
	shotlist
}: {
	value: TPlatformValue
	shotlist: IShotlist
}): TPlatformResult | null => {
	const lang = PLATFORMS_CODES[value]
	const originalLang = shotlist.original?.language
	const hasOriginalContent = !!shotlist.original?.content

	const translationsByLang = new Set(
		(shotlist.translations || []).filter(t => !!t.content).map(t => t.language)
	)

	if (lang === originalLang && hasOriginalContent) {
		return 'transcription'
	}

	if (translationsByLang.has(lang)) {
		return 'translation'
	}

	return null
}

export const createIdlePlatformState = ({
	value,
	autoFilling
}: {
	value: TPlatformValue
	autoFilling?: boolean
}): ISelectedPlatform => ({
	value,
	headerState: 'idle',
	autoFilling
})

export const createDonePlatformState = ({
	value,
	result,
	autoFilling
}: {
	value: TPlatformValue
	result: TPlatformResult
	autoFilling?: boolean
}): ISelectedPlatform => ({
	value,
	headerState: 'done',
	result,
	autoFilling
})

export const createProcessingPlatformPatch = (
	processing: TPlatformProcessingType
): Partial<ISelectedPlatform> => {
	if (processing === 'autofill') {
		return {
			processing,
			errorText: undefined
		}
	}

	return {
		headerState: 'processing',
		processing,
		errorText: undefined,
		result: undefined
	}
}

export const createDonePlatformPatch = (
	result: TPlatformResult
): Partial<ISelectedPlatform> => ({
	headerState: 'done',
	processing: undefined,
	result,
	errorText: undefined
})

export const createAutoFillFinishedPatch = (): Partial<ISelectedPlatform> => ({
	processing: undefined,
	errorText: undefined
})

export const createErrorPlatformPatch = (
	errorText?: string
): Partial<ISelectedPlatform> => ({
	headerState: 'error',
	processing: undefined,
	result: undefined,
	errorText: errorText || 'Something went wrong'
})

export const createRefreshedDonePlatformState = ({
	platform,
	result
}: {
	platform: ISelectedPlatform
	result: TPlatformResult
}): ISelectedPlatform => ({
	...platform,
	headerState: 'done',
	result,
	processing: undefined,
	errorText: undefined,
	sourceLangCode: undefined
})

export const createRefreshedIdlePlatformState = ({
	platform
}: {
	platform: ISelectedPlatform
}): ISelectedPlatform => ({
	...platform,
	headerState: 'idle',
	result: undefined,
	processing: undefined,
	errorText: undefined,
	sourceLangCode: undefined
})

export const createResetHeaderStatePatch = (
	state: TPlatformHeaderKind
): Partial<ISelectedPlatform> => ({
	headerState: state
})
